import { useEffect, useState } from "react";

// ─────────────────────────────────────────────────────────────
//  ToastContainer
//
//  Affiche la liste des toasts en bas à droite de l'écran.
//  Chaque toast entre par la droite et sort vers la droite.
//
//  Props :
//    toasts      → tableau de { id, message, type }
//    onRemove    → fonction(id) appelée au clic sur ×
//
//  Usage :
//    <ToastContainer toasts={toasts} onRemove={removeToast} />
// ─────────────────────────────────────────────────────────────

// ── Couleurs et icônes selon le type ────────────────────────
const STYLES = {
  success: {
    icon:       "bi-check-circle-fill",
    color:      "var(--accent, #00d4aa)",
    background: "rgba(0, 212, 170, 0.12)",
    border:     "rgba(0, 212, 170, 0.3)",
  },
  error: {
    icon:       "bi-x-circle-fill",
    color:      "#f87171",
    background: "rgba(248, 113, 113, 0.12)",
    border:     "rgba(248, 113, 113, 0.3)",
  },
  info: {
    icon:       "bi-info-circle-fill",
    color:      "#60a5fa",
    background: "rgba(96, 165, 250, 0.12)",
    border:     "rgba(96, 165, 250, 0.3)",
  },
};

// ── Un seul toast ────────────────────────────────────────────
function Toast({ id, message, type, onRemove }) {
  // `visible` contrôle l'animation d'entrée/sortie
  const [visible, setVisible] = useState(false);

  // Entrée : on attend 10ms pour que le CSS de transition s'applique
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const s = STYLES[type] ?? STYLES.success;

  // Clic sur × → animation de sortie, puis suppression
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onRemove(id), 300); // 300ms = durée de la transition
  };

  return (
    <div
      style={{
        display:       "flex",
        alignItems:    "center",
        gap:           12,
        padding:       "12px 16px",
        borderRadius:  12,
        border:        `1px solid ${s.border}`,
        background:    s.background,
        backdropFilter: "blur(12px)",
        boxShadow:     "0 8px 32px rgba(0,0,0,0.3)",
        minWidth:      280,
        maxWidth:      380,
        cursor:        "default",

        // Animation slide-in depuis la droite
        transform:   visible ? "translateX(0)"    : "translateX(110%)",
        opacity:     visible ? 1                  : 0,
        transition:  "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease",
      }}
    >
      {/* Icône */}
      <i
        className={`bi ${s.icon}`}
        style={{ fontSize: 18, color: s.color, flexShrink: 0 }}
      />

      {/* Message */}
      <span style={{ flex: 1, fontSize: 13, color: "var(--text-1, #e2e8f0)", lineHeight: 1.4 }}>
        {message}
      </span>

      {/* Bouton fermer */}
      <button
        onClick={handleClose}
        style={{
          background: "none",
          border:     "none",
          cursor:     "pointer",
          color:      "var(--text-3, #64748b)",
          fontSize:   14,
          padding:    2,
          display:    "flex",
          flexShrink: 0,
        }}
      >
        <i className="bi bi-x" />
      </button>
    </div>
  );
}

// ── Conteneur positionné en bas à droite ─────────────────────
export default function ToastContainer({ toasts, onRemove }) {
  if (!toasts.length) return null;

  return (
    <div
      style={{
        position:      "fixed",
        top:           24,
        right:         24,
        zIndex:        9999,
        display:       "flex",
        flexDirection: "column",
        gap:           10,
        pointerEvents: "none", // le conteneur ne bloque pas les clics
      }}
    >
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: "auto" }}>
          <Toast
            id={t.id}
            message={t.message}
            type={t.type}
            onRemove={onRemove}
          />
        </div>
      ))}
    </div>
  );
}